import Translate, { useI18n } from '@hydra/common/components/i18n';
import Icon from '@hydra/common/components/Icon';
import ReviewModule from '@hydra/common/components/Review/ReviewModule';
import ReviewDashboard from '@hydra/common/components/ReviewDashboard';
import { useReviewContext } from '@hydra/common/context/review';
import DescriptionCrit from '@hydra/common/criteria/description';
import GooglePreviewCrit from '@hydra/common/criteria/google_preview';
import TitleCrit from '@hydra/common/criteria/title';
import useReviewFilter from '@hydra/common/customHooks/useReviewFilter';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import classnames from 'classnames';
import React from 'react';
import ReviewFilter from '../ReviewFilter';
import FloatingCTA from './ReviewTeaserFloatingCTA';
import ReviewTeaserPreviewFull from './ReviewTeaserPreviewFull';

const useStyles = makeStyles(theme => ({
  wrapper: {
    marginBottom: 100,
    position: 'relative'
  },
  heading: {
    fontWeight: theme.typography.fontWeightSemibold,
    lineHeight: 1.3,
    marginBottom: theme.spacing(0.5),
    '& span': {
      color: theme.palette.primary.main
    }
  },
  sectionWrapper: { marginBottom: theme.spacing(6) },
  lockedCrit: {
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      zIndex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end',
      background: 'linear-gradient(180deg, rgba(255,255,255,0) 0, rgba(255,255,255,1) 100%)'
    }
  },
  signUpBtn: {
    position: 'absolute',
    left: '50%',
    bottom: 40,
    transform: 'translateX(-50%)',
    zIndex: 1,
    fontSize: theme.typography.fontSizeLarge,
    lineHeight: 'normal',
    padding: theme.spacing(2, 2.25),
    [theme.breakpoints.down('xs')]: {
      width: '90%',
      fontSize: theme.typography.fontSizeBase
    }
  },
  cta: {
    [theme.breakpoints.down('md')]: {
      display: 'none'
    },
    top: 92,
    position: 'absolute',
    right: -160
  },
  unlock: { marginRight: theme.spacing(1) },
  filterWrapper: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 0)
  }
}));

export default function ReviewTeaserPreview ({ data }) {
  const { variant } = useReviewContext();
  const {
    review: {
      url,
      competitors,
      screenshot,
      score,
      updatedAt,
      progress,
      pdfTemplates = [],
      criteria
    },
    reviewStructure,
    totalCrits
  } = data;
  const {
    handleFilter,
    handleSelect,
    filters,
    showCrit,
    showModule,
    showSection
  } = useReviewFilter({ criteria, reviewStructure });
  const classes = useStyles();
  const { locale } = useI18n();

  return (
    <Container
      maxWidth='md'
      className={classes.wrapper}
    >
      <Translate
        content='free_review_your_free_review_heading'
        with={{ url }}
        component='h2'
        className={classes.heading}
      />
      <div className={classes.sectionWrapper}>
        <ReviewDashboard
          url={url}
          competitors={competitors}
          screenshot={screenshot}
          score={score}
          updatedAt={updatedAt}
          progress={progress}
          pdfTemplates={pdfTemplates}
        />
        <FloatingCTA className={classes.cta} />
      </div>
      {
        !variant &&
        (
          <>
            <Typography
              className={classes.sectionHeading}
              variant='h2'
              color='textPrimary'
            >
              <Translate content='free_review_on_page_heading' />
            </Typography>
            <ReviewModule module='content'>
              <TitleCrit
                criterium={criteria.title}
                competitors={competitors}
                url={url}
              />
              <DescriptionCrit
                criterium={criteria.description}
                competitors={competitors}
                url={url}
              />
              <div className={classes.lockedCrit}>
                <GooglePreviewCrit
                  criterium={criteria.google_preview}
                  competitors={competitors}
                  url={url}
                />
                <Button
                  variant='contained'
                  size='large'
                  color='primary'
                  className={classes.signUpBtn}
                  href={`/${locale}/register/plan/v2pro`}
                >
                  <Icon
                    type='unlock'
                    color='white'
                    className={classes.unlock}
                  />
                  <Translate
                    content='free_review_show_more'
                    with={{ count: totalCrits }}
                  />
                </Button>
              </div>
            </ReviewModule>
          </>
        )
      }
      {
        ['noadvice', 'nocrit'].includes(variant) && (
          <>
            <div className={classnames(classes.filterWrapper, 'nr-review-filterbar')}>
              <ReviewFilter
                filters={filters}
                handleFilter={handleFilter}
                handleSelect={handleSelect}
              />
            </div>
            <ReviewTeaserPreviewFull
              criteria={criteria}
              url={url}
              competitors={competitors}
              reviewStructure={reviewStructure}
              showCrit={showCrit}
              showModule={showModule}
              showSection={showSection}
              onpageReviewFilter={null}
            />
          </>
        )
      }
    </Container>
  );
}
